import { useMutation } from '@apollo/client/react';
import { useCallback } from 'react';

import { useIntl } from '~/contexts/intl';
import { ProfileEditableFields } from '~/screens/profile';
import { ProfileFieldEditDocument, ProfileImageUploadDocument } from '~/types';
import { ReactNativeFile } from '~/utils';

export function useProfileEdit(memberId: ID) {
    const [updateProfileFields] = useMutation(ProfileFieldEditDocument);
    const [uploadProfileImage] = useMutation(ProfileImageUploadDocument);
    const { setCurrentLocale } = useIntl();

    // Upload profile image to the server. On web, expo-image-picker returns a data: URI
    // and optionally a native File object. On mobile, it returns a file:// URI.
    //
    // Important: We cannot use fetch() to convert data: or blob: URIs to Blobs on the
    // deployed web app because the CloudFront CSP connect-src directive does not include
    // data: or blob: schemes, causing the browser to block such fetch() calls.
    // Instead, we use the native File object from the picker when available, or manually
    // decode the base64 data: URI into a Blob.
    const profileImageUpload = useCallback(
        async (uri: string, pickerFile?: File) => {
            let file: File | Blob | ReactNativeFile;
            if (pickerFile) {
                // Web: use the native File object provided by expo-image-picker directly,
                // bypassing any URI-based conversion that would be blocked by CSP
                file = pickerFile;
            } else if (uri.startsWith('data:')) {
                // Web fallback: convert data: URI to Blob manually using atob + Uint8Array.
                // This avoids fetch() which would be blocked by CSP connect-src on the
                // deployed web app (CloudFront does not include data: in connect-src).
                const [header, base64Data] = uri.split(',');
                const mimeType = header.match(/:(.*?);/)?.[1] || 'image/jpeg';
                const byteString = atob(base64Data);
                const bytes = new Uint8Array(byteString.length);
                for (let i = 0; i < byteString.length; i++) {
                    bytes[i] = byteString.charCodeAt(i);
                }
                file = new Blob([bytes], { type: mimeType });
            } else {
                // Mobile: create a ReactNativeFile from the file:// URI. React Native's
                // networking layer resolves the URI when the file is uploaded.
                const name = uri.substring(uri.lastIndexOf('/') + 1);
                file = new ReactNativeFile({ uri, name, type: 'image/jpg' });
            }

            await uploadProfileImage({
                variables: { input: { file } },
                optimisticResponse: {
                    avatarEdit: {
                        member: {
                            id: memberId,
                            avatarBigUrl: uri,
                            __typename: 'MemberNode'
                        }
                    }
                }
            });
        },
        [memberId, uploadProfileImage]
    );

    const profileFieldUpdate = useCallback(
        async (fields: Partial<ProfileEditableFields>) => {
            if ('lang' in fields && fields.lang) {
                await updateProfileFields({
                    variables: { input: { ...fields } },
                    update: cache => {
                        cache.evict({ id: 'ROOT_QUERY', fieldName: 'root' });
                        cache.gc();
                    }
                });
                await setCurrentLocale(fields.lang);
            } else {
                await updateProfileFields({
                    variables: { input: { ...fields } }
                });
            }
        },
        [setCurrentLocale, updateProfileFields]
    );

    return { profileFieldUpdate, profileImageUpload };
}
